import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  HStack,
  Stack,
  Button,
  Image,
  Input,
  Alert,
  AlertIcon,
  AlertTitle,
  CloseButton,
  useToast,
  Center,
  VStack,
  Text,
} from '@chakra-ui/react';
import axios from 'axios';
import { ColorPickerAtom } from '../../atoms/ColorPickerAtom';
import { ToggleAtom } from '../../atoms/CustomToggle';
import { CustomText } from '../../atoms';
import { useSettings } from '../../../stateManagement/SettingsContext';
import { updateConfigVersion } from '../../../utils';

interface BannerSettingsMoleculeProps {
  configs: {
    bannerImageUrl: string;
    showBanner: boolean;
    showOverlay: boolean;
    showSearchBar: boolean;
    bannerBgColor: string;
    bannerTextColor: string;
    bannerTextSize?: string;
    isBannerFullScreen: boolean;
  };
  onConfigChange: (key: string, value: any) => void;
  convertImageToBase64: (file: File) => Promise<string | ArrayBuffer | null>;
}
const BLOB_CONTAINER_NAME = process.env.REACT_APP_BLOB_CONTAINER_NAME || '';

export const BannerSettingsMolecule: React.FC<BannerSettingsMoleculeProps> = ({
  configs,
  onConfigChange,
  convertImageToBase64,
}) => {
  const [showOverlay, setShowOverlay] = useState(configs.showOverlay);
  const [showSearchBar, setShowSearchBar] = useState(configs.showSearchBar);
  const [showBanner, setShowBanner] = useState(configs.showBanner);
  const [isBannerFullScreen, setIsBannerFullScreen] = useState(configs.isBannerFullScreen);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [uploadType, setUploadType] = useState<'banner' | 'playerHub' | 'myTicket'>('banner');
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingPlayerHub, setUploadingPlayerHub] = useState(false);
  const [uploadingMyTicket, setUploadingMyTicket] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<{
    banner: string | null;
    playerHub: string | null;
    myTicket: string | null;
  }>({
    banner: null,
    playerHub: null,
    myTicket: null,
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const toast = useToast();
  const { adminApiKey, globalConfigData } = useSettings();

  useEffect(() => {
    setShowOverlay(configs.showOverlay);
    setShowSearchBar(configs.showSearchBar);
    setShowBanner(configs.showBanner);
    setIsBannerFullScreen(configs.isBannerFullScreen);
  }, [configs]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const resetFileInput = () => {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setPreviewUrls(prev => ({
        ...prev,
        [uploadType]: null,
      }));
    };

    const file = event.target.files?.[0];

    if (file) {
      if (!file.type.startsWith('image/')) {
        setAlertMessage('Only image files are allowed.');
        resetFileInput();
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setAlertMessage('File size must be less than 5MB.');
        resetFileInput();
        return;
      }

      let fileName = 'banner.png';
      if (uploadType === 'playerHub') fileName = 'ticketViewPageLogoUrl.png';
      if (uploadType === 'myTicket') fileName = 'myTicketPageLogoUrl.png';

      const renamedFile = new File([file], fileName, { type: file.type });
      setSelectedFile(renamedFile);

      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setPreviewUrls(prev => ({
          ...prev,
          [uploadType]: result,
        }));
      };
      reader.readAsDataURL(renamedFile);
    }
  };

  // Add timestamp version to the image URL
  const addVersionParam = (url: string, timestamp: number) => {
    if (!url) return url;

    // Remove existing version param (?v= or &v=)
    let cleanedUrl = url.replace(/[?&]v=\d+/, '');

    // Always use ?v= (even if other query params exist, remove all of them)
    const [baseUrl] = cleanedUrl.split('?'); // use destructuring instead of accessing index
    cleanedUrl = baseUrl;

    return `${cleanedUrl}?v=${timestamp}`;
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const setUploadFlag = (value: boolean) => {
      if (uploadType === 'banner') setUploadingBanner(value);
      else if (uploadType === 'playerHub') setUploadingPlayerHub(value);
      else if (uploadType === 'myTicket') setUploadingMyTicket(value);
    };

    setUploadFlag(true);

    try {
      // Step 1: Fetch config
      const configResponse = await fetch(`${process.env.REACT_APP_CDN_URL}/config.json`);
      const configData = await configResponse.json();

      // Step 2: Add version param to appropriate image field
      const timestamp = Date.now();
      if (uploadType === 'banner') {
        configData.configs.bannerImageUrl = addVersionParam(
          configData.configs.bannerImageUrl,
          timestamp,
        );
      } else if (uploadType === 'playerHub') {
        configData.configs.ticketViewPageLogoUrl = addVersionParam(
          configData.configs.ticketViewPageLogoUrl,
          timestamp,
        );
      } else if (uploadType === 'myTicket') {
        configData.configs.myTicketPageLogoUrl = addVersionParam(
          configData.configs.myTicketPageLogoUrl,
          timestamp,
        );
      }

      // Step 3: Convert image to base64
      const base64String = await convertImageToBase64(selectedFile);

      // Step 4: Prepare payload
      const data = JSON.stringify([
        {
          name: selectedFile.name,
          type: 'image',
          path: `${BLOB_CONTAINER_NAME}/images`,
          data: base64String,
        },
        {
          name: 'config.json',
          type: 'json',
          path: `${BLOB_CONTAINER_NAME}`,
          data: JSON.stringify(configData, null, 2),
        },
        {
          name: 'globalConfig.json',
          type: 'json',
          path: `${BLOB_CONTAINER_NAME}`,
          data: JSON.stringify(updateConfigVersion(globalConfigData ?? undefined)),
        },
      ]);

      // Step 5: Upload via Axios
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        // url: 'https://catalysthc.ms.helpshift.live/api/fe-config',
        url: 'https://demo-hc-api.ms.helpshift.live/api/fe-config',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${adminApiKey ?? ''}`,
        },
        data,
      };

      const response = await axios.request(config);

      if (response.status >= 200 && response.status < 300) {
        toast({
          title: 'Upload successful!',
          description: `${selectedFile.name} has been uploaded.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error uploading file:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while uploading.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setUploadFlag(false);
    }
  };

  return (
    <Box position="relative" height="80vh" overflowY="auto">
      <Stack spacing={6} p={4} mt={4}>
        {/* Banner Upload */}
        <VStack p={4} borderWidth="1px" borderRadius="md" mb={4} align="start">
          <CustomText>Upload Banner Image</CustomText>
          <HStack spacing={4}>
            <Input
              type="file"
              accept="image/png, image/jpg, image/jpeg"
              onClick={() => setUploadType('banner')}
              onChange={handleFileChange}
              ref={fileInputRef}
              bg="white"
            />
            <Button
              colorScheme="blue"
              onClick={handleUpload}
              isDisabled={!selectedFile || uploadType !== 'banner'}
              isLoading={uploadingBanner}
            >
              Upload
            </Button>
          </HStack>
          <Text fontSize="sm" color="gray.500" mt={2}>
            Supported file types: <b>png, jpg, jpeg</b>
          </Text>
          <Text fontSize="sm" color="gray.500">
            Note: This will upload immediately without further confirmation.
          </Text>
        </VStack>
        {previewUrls.banner && (
          <Box mt={4} display="flex" justifyContent="center">
            <Image
              src={previewUrls.banner}
              alt="Banner Preview"
              boxSize="200px"
              borderRadius="md"
              objectFit="cover"
            />
          </Box>
        )}

        {/* Player Hub Upload */}
        <VStack p={4} borderWidth="1px" borderRadius="md" mb={4} align="start">
          <CustomText>Upload Player Hub Image</CustomText>
          <HStack spacing={4}>
            <Input
              type="file"
              accept="image/png, image/jpg, image/jpeg"
              onClick={() => setUploadType('playerHub')}
              onChange={handleFileChange}
              ref={fileInputRef}
              bg="white"
            />
            <Button
              colorScheme="blue"
              onClick={handleUpload}
              isDisabled={!selectedFile || uploadType !== 'playerHub'}
              isLoading={uploadingPlayerHub}
            >
              Upload
            </Button>
          </HStack>
          <Text fontSize="sm" color="gray.500" mt={2}>
            Supported file types: <b>png, jpg, jpeg</b>
          </Text>
          <Text fontSize="sm" color="gray.500">
            Note: This will upload immediately without further confirmation.
          </Text>
        </VStack>
        {previewUrls.playerHub && (
          <Box mt={4} display="flex" justifyContent="center">
            <Image
              src={previewUrls.playerHub}
              alt="Player Hub Preview"
              boxSize="200px"
              borderRadius="md"
              objectFit="cover"
            />
          </Box>
        )}

        {/* My Ticket Upload */}
        <VStack p={4} borderWidth="1px" borderRadius="md" mb={4} align="start">
          <CustomText>Upload My Ticket Section Image</CustomText>
          <HStack spacing={4}>
            <Input
              type="file"
              accept="image/png, image/jpg, image/jpeg"
              onClick={() => setUploadType('myTicket')}
              onChange={handleFileChange}
              ref={fileInputRef}
              bg="white"
            />
            <Button
              colorScheme="blue"
              onClick={handleUpload}
              isDisabled={!selectedFile || uploadType !== 'myTicket'}
              isLoading={uploadingMyTicket}
            >
              Upload
            </Button>
          </HStack>
          <Text fontSize="sm" color="gray.500" mt={2}>
            Supported file types: <b>png, jpg, jpeg</b>
          </Text>
          <Text fontSize="sm" color="gray.500">
            Note: This will upload immediately without further confirmation.
          </Text>
        </VStack>
        {previewUrls.myTicket && (
          <Box mt={4} display="flex" justifyContent="center">
            <Image
              src={previewUrls.myTicket}
              alt="My Ticket Preview"
              boxSize="200px"
              borderRadius="md"
              objectFit="cover"
            />
          </Box>
        )}

        <Stack spacing={4}>
          <HStack
            p={4}
            borderWidth="1px"
            borderRadius="md"
            mb={4}
            justifyContent="space-between"
            alignItems="center"
          >
            <ToggleAtom
              justifyContent="space-between"
              label="Show Banner"
              value={showBanner}
              onChange={value => {
                setShowBanner(value);
                onConfigChange('showBanner', value);
              }}
            />
          </HStack>

          <HStack
            p={4}
            borderWidth="1px"
            borderRadius="md"
            mb={4}
            justifyContent="space-between"
            alignItems="center"
          >
            <ToggleAtom
              justifyContent="space-between"
              label="Show Banner in full screen"
              value={isBannerFullScreen}
              onChange={value => {
                setIsBannerFullScreen(value);
                onConfigChange('isBannerFullScreen', value);
              }}
            />
          </HStack>

          <HStack
            p={4}
            borderWidth="1px"
            borderRadius="md"
            mb={4}
            justifyContent="space-between"
            alignItems="center"
          >
            <ToggleAtom
              justifyContent="space-between"
              label="Show Overlay"
              value={showOverlay}
              onChange={value => {
                setShowOverlay(value);
                onConfigChange('showOverlay', value);
              }}
            />
          </HStack>

          <HStack
            p={4}
            borderWidth="1px"
            borderRadius="md"
            mb={4}
            justifyContent="space-between"
            alignItems="center"
          >
            <ToggleAtom
              justifyContent="space-between"
              label="Show SearchBar"
              value={showSearchBar}
              onChange={value => {
                setShowSearchBar(value);
                onConfigChange('showSearchBar', value);
              }}
            />
          </HStack>
          <HStack
            p={4}
            borderWidth="1px"
            borderRadius="md"
            mb={4}
            justifyContent="space-between"
            alignItems="center"
          >
            <ColorPickerAtom
              label="Banner Text Color"
              value={configs.bannerTextColor}
              onChange={color => onConfigChange('bannerTextColor', color)}
            />
          </HStack>
        </Stack>
      </Stack>

      {alertMessage && (
        <Center
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex="1000"
        >
          <Alert status="error" variant="solid" borderRadius="md" boxShadow="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Error</AlertTitle>
              {alertMessage}
            </Box>
            <CloseButton
              onClick={() => setAlertMessage(null)}
              position="absolute"
              right="8px"
              top="8px"
            />
          </Alert>
        </Center>
      )}
    </Box>
  );
};
